/** How a new business intends to operate — drives onboarding route (vendor vs pro). */
export const SIGNUP_SERVICE_INTENTS = [
  'trades',
  'appointments',
  'creative',
  'rentals',
  'transport',
] as const;

export type SignupServiceIntent = (typeof SIGNUP_SERVICE_INTENTS)[number];

export const SIGNUP_SERVICE_INTENT_LABELS: Record<SignupServiceIntent, string> = {
  trades: 'Trades & repairs',
  appointments: 'Appointments (salon, spa, fitness)',
  creative: 'Events, photo & creative',
  rentals: 'Rentals (cars, equipment)',
  transport: 'Transport & shuttles',
};

export const SIGNUP_SERVICE_INTENT_HINTS: Record<SignupServiceIntent, string> = {
  trades: 'Electricians, plumbers, AC — quote-based jobs',
  appointments: 'Book time slots with clients',
  creative: 'Photography, catering, DJs, marketing',
  rentals: 'Vehicle and equipment hire',
  transport: 'Taxi, tours, delivery, shuttles',
};

/** Vendor onboarding query defaults per service intent. */
export const SIGNUP_INTENT_VENDOR_ONBOARD: Record<
  Exclude<SignupServiceIntent, 'trades'>,
  { sector: string; directoryCategoryId: string; modes: ('book' | 'hire' | 'shop')[] }
> = {
  appointments: { sector: 'beauty', directoryCategoryId: 'beauty-hair-salon', modes: ['book'] },
  creative: { sector: 'services', directoryCategoryId: 'svc-other', modes: ['hire', 'book'] },
  rentals: { sector: 'services', directoryCategoryId: 'svc-rental', modes: ['book', 'hire'] },
  transport: { sector: 'services', directoryCategoryId: 'svc-transport', modes: ['book', 'hire'] },
};

export function parseSignupServiceIntent(raw: string | null | undefined): SignupServiceIntent | null {
  if (!raw) return null;
  const n = raw.trim().toLowerCase();
  return (SIGNUP_SERVICE_INTENTS as readonly string[]).includes(n) ? (n as SignupServiceIntent) : null;
}

export function isTradesServiceIntent(intent: SignupServiceIntent | null | undefined): boolean {
  return intent === 'trades';
}

/** True when signup should complete vendor onboarding (not pro-only). */
export function signupUsesVendorOnboarding(intent: SignupServiceIntent | null | undefined): boolean {
  return intent != null && intent !== 'trades';
}
